import {
  type PostApiAdminProvisionTestResponse,
  usePostApiAdminUpdatePermissions,
} from '@sit/client-shared';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/lib/components/flat-table';
import Loader from 'carbon-react/lib/components/loader';
import toastr from '../../helpers/toastr';

const copyToClipboard = () => {
  const elTable = document.querySelector('table');
  let sel: Selection | null = null;
  // Ensure that range and selection are supported by the browsers
  if (elTable && document.createRange && window.getSelection) {
    const range = document.createRange();
    sel = window.getSelection();
    // unselect any element in the page
    sel?.removeAllRanges();
    try {
      range.selectNodeContents(elTable);
      sel?.addRange(range);
    } catch {
      range.selectNode(elTable);
      sel?.addRange(range);
    }
    navigator.clipboard.writeText(elTable.innerText);
  }
  sel?.removeAllRanges();
  toastr.success('Table copied to clipboard');
};

interface MissingPermissionsProps {
  missing: PostApiAdminProvisionTestResponse['missingPermissions'];
  companyTreeSettingsId: number | undefined;
}

function MissingPermissions({
  missing,
  companyTreeSettingsId,
}: MissingPermissionsProps) {
  const {
    mutate: updatePermissions,
    isPending: isUpdating,
    error,
  } = usePostApiAdminUpdatePermissions({
    onSuccess: () => {
      toastr.success(
        'Permissions scheduled for update (this may take a few minutes).',
      );
      toastr.warning(
        'You will not receive a notification when the update is complete. Please check back later to see if the permissions have been updated.',
      );
    },
  });
  return (
    <>
      <Box mt={2}>
        <small>
          Please note that if you recently made changes to the permissions in
          Intacct, it might take up to 2 minutes for the changes to apply. If
          you get the same result after making changes, please try again after a
          minute.
        </small>
      </Box>
      <Box mt={2}>
        <b>The WSU is missing the following permissions:</b>
      </Box>
      <FlatTable size="small" mt={2}>
        <FlatTableHead>
          <FlatTableRow>
            <FlatTableHeader>Application/Module</FlatTableHeader>
            <FlatTableHeader>Activities/Lists</FlatTableHeader>
            <FlatTableHeader>Required Permissions</FlatTableHeader>
          </FlatTableRow>
        </FlatTableHead>
        <FlatTableBody>
          {missing.map((m) => (
            <FlatTableRow key={`${m.module}-${m.activity}`}>
              <FlatTableCell>{m.module}</FlatTableCell>
              <FlatTableCell>{m.activity}</FlatTableCell>
              <FlatTableCell>{m.permissions.join(', ')}</FlatTableCell>
            </FlatTableRow>
          ))}
        </FlatTableBody>
      </FlatTable>
      <Box
        mt={2}
        display="flex"
        gap={2}
        flexDirection="row"
        justifyContent="flex-end"
      >
        {companyTreeSettingsId != null && (
          <Button
            buttonType="primary"
            onClick={() =>
              updatePermissions({
                body: {
                  companyTreeSettingsId,
                },
              })
            }
            disabled={isUpdating}
          >
            {error ? (
              <span style={{ color: 'red' }}>{error.message}</span>
            ) : null}
            {isUpdating ? (
              <Loader isInsideButton />
            ) : (
              'Schedule update permissions'
            )}
          </Button>
        )}
        <Button onClick={() => copyToClipboard()}>Copy</Button>
      </Box>
    </>
  );
}

export default MissingPermissions;
